import type { Permission } from '@field-support/shared';

/**
 * The navigation model: one entry per page, with the permissions that gate it.
 * Routing, the nav rail and the home redirect all derive from this single list,
 * so a page PR adds exactly one entry here.
 */
export interface NavItem {
  path: string;
  labelKey: string;
  section: 'overview' | 'configuration' | 'catalog' | 'administration';
  /** Visible when the user has ANY of these (empty = every signed-in user). */
  permissions: Permission[];
}

/** Roles that may read the catalog (the backend's readCatalog gate, minus assignVisits which has no catalog page). */
const CATALOG_READERS: Permission[] = [
  'manageBrands',
  'manageBranches',
  'bulkImportCatalog',
  'reviewUnlistedBranches',
  'viewAnalytics',
  'viewAnomalies',
];

// Pages land one PR at a time; each adds its entry (see README).
export const NAV_ITEMS: NavItem[] = [
  // Performance: the backend's viewAnalytics gate on /dashboard/kpis and /dashboard/issues.
  { path: '/dashboard', labelKey: 'nav.dashboard', section: 'overview', permissions: ['viewAnalytics'] },
  // Same permission as the dashboard: the visits table is the numbers behind it.
  { path: '/visits', labelKey: 'nav.visits', section: 'overview', permissions: ['viewAnalytics'] },
  // The manager's planning board: team scope is enforced server-side per viewer.
  { path: '/assignments', labelKey: 'nav.assignments', section: 'overview', permissions: ['assignVisits'] },
  // The list and thresholds read are viewAnomalies; saving thresholds is SUPER_ADMIN only (checked in the page).
  { path: '/anomalies', labelKey: 'nav.anomalies', section: 'overview', permissions: ['viewAnomalies'] },
  // Reading is viewAnalytics; the row's retry also needs manageZoho (checked in the page).
  { path: '/sync-issues', labelKey: 'nav.syncIssues', section: 'overview', permissions: ['viewAnalytics'] },
  // Visit gaps (summary, day, recompute) are all the backend's viewAnalytics gate.
  { path: '/reports/travel', labelKey: 'nav.travel', section: 'overview', permissions: ['viewAnalytics'] },
  // The daily summaries read with either; the route link inside also needs viewAgentLocation (checked in the page).
  { path: '/reports/activity', labelKey: 'nav.activity', section: 'overview', permissions: ['viewAnalytics', 'viewAgentLocation'] },
  // Anyone's location is viewAgentLocation (audited server-side).
  { path: '/tracking/live', labelKey: 'nav.liveMap', section: 'overview', permissions: ['viewAgentLocation'] },
  { path: '/tracking/route', labelKey: 'nav.route', section: 'overview', permissions: ['viewAgentLocation'] },
  // The visit form and its Zoho mapping: the backend's manageForms gate.
  { path: '/forms', labelKey: 'nav.forms', section: 'configuration', permissions: ['manageForms'] },
  // The Zoho Desk connection: the backend's manageZoho gate. The OAuth callback redirects here.
  { path: '/integrations/zoho', labelKey: 'nav.zoho', section: 'configuration', permissions: ['manageZoho'] },
  { path: '/catalog/brands', labelKey: 'nav.brands', section: 'catalog', permissions: CATALOG_READERS },
  { path: '/catalog/branches', labelKey: 'nav.branches', section: 'catalog', permissions: CATALOG_READERS },
  // Reading zones and areas is the catalog readers' gate; editing them (and moving branches) is manageBranches (checked in the page).
  { path: '/catalog/geography', labelKey: 'nav.geography', section: 'catalog', permissions: CATALOG_READERS },
  // Creates merchants and branches from a file: the backend's bulkImportCatalog gate, as in the old navigation.
  { path: '/catalog/import', labelKey: 'nav.import', section: 'catalog', permissions: ['bulkImportCatalog'] },
  // Suggestions, approve/reject and setting a location: the backend's manageBranches gate.
  { path: '/catalog/locations', labelKey: 'nav.locations', section: 'catalog', permissions: ['manageBranches'] },
  // The review is reviewUnlistedBranches; adding a branch also needs manageBranches, an option manageForms (checked in the page).
  { path: '/catalog/alerts', labelKey: 'nav.alerts', section: 'catalog', permissions: ['reviewUnlistedBranches'] },
  { path: '/users', labelKey: 'nav.users', section: 'administration', permissions: ['manageUsers'] },
  // Device policy belongs with user management (D93), hence the same permission.
  { path: '/users/devices', labelKey: 'nav.devices', section: 'administration', permissions: ['manageUsers'] },
  // Compose, history and the test to oneself: the backend's sendNotifications gate on /engine/notifications.
  { path: '/notifications', labelKey: 'nav.notifications', section: 'administration', permissions: ['sendNotifications'] },
  // Mobile app distribution (APK upload, install links, minimum version) is hidden: the app ships
  // through the App Store and Google Play. Uncomment this item and its '/settings' entry in pages.ts to restore it.
  // Upload/publish builds, install links, endpoints: the backend's manageAppDistribution gate.
  // { path: '/settings', labelKey: 'nav.settings', section: 'administration', permissions: ['manageAppDistribution'] },
];
